import {
  appBridgeBootstrapSchema,
  createAppBridgeClient,
  type AppBridgeClient,
  type AppBridgeContext,
} from "@crewfsm/app-bridge";

export interface PipelineAppSession {
  bridge: AppBridgeClient;
  context: AppBridgeContext;
  sessionToken: string;
}

export async function connectPipelineApp(): Promise<PipelineAppSession> {
  if (window.parent === window) {
    throw new Error("Open Pipeline from the CrewFSM Apps navigation.");
  }
  const bootstrap = parseBootstrap(window.name);
  const hostOrigin = parseHostOrigin(document.referrer);
  let diagnostic = "No host response was received.";
  const recordHostResponse = (event: MessageEvent) => {
    const data = isRecord(event.data) ? event.data : {};
    diagnostic = [
      `origin=${event.origin || "empty"}`,
      `source=${event.source === window.parent ? "parent" : "other"}`,
      `protocol=${String(data.protocolVersion ?? "missing")}`,
      `type=${String(data.type ?? "missing")}`,
      `installation=${String(data.installationId ?? "missing")}`,
    ].join(", ");
  };
  window.addEventListener("message", recordHostResponse);
  const bridge = createAppBridgeClient({
    installationId: bootstrap.installationId,
    hostOrigin,
    eventSource: window,
    hostWindow: window.parent,
    expectedHostWindow: window.parent,
    currentRoute: window.location.pathname || "/overview",
    clientVersion: "1.0.0",
  });
  try {
    const context = await bridge.connect();
    return {
      bridge,
      context,
      sessionToken: bridge.getSessionToken(),
    };
  } catch (error) {
    bridge.destroy();
    const message = error instanceof Error ? error.message : "Unknown error";
    const diagnosticSuffix = import.meta.env.DEV ? ` (${diagnostic})` : "";
    throw new Error(`${message}${diagnosticSuffix}`, { cause: error });
  } finally {
    window.removeEventListener("message", recordHostResponse);
  }
}

export function resolvePipelineView(
  currentRoute: string,
): "OVERVIEW" | "LEADS" {
  return currentRoute.endsWith("/leads") ? "LEADS" : "OVERVIEW";
}

function parseBootstrap(value: string) {
  try {
    return appBridgeBootstrapSchema.parse(JSON.parse(value));
  } catch {
    throw new Error("CrewFSM did not provide a valid app session bootstrap.");
  }
}

function parseHostOrigin(referrer: string): string {
  try {
    return new URL(referrer).origin;
  } catch {
    throw new Error("CrewFSM host origin is unavailable.");
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
